/**
 * useRecommendations — fetch AI-adaptive course recommendations for the agent.
 *
 * EPIC-08-10b AC-8: Data is cached for 5 minutes (stale-while-revalidate pattern)
 * so tab navigation does not trigger unnecessary refetches.
 *
 * Returns:
 *   recommendations — list of ranked Recommendation objects (empty when insufficient data)
 *   isLoading       — true while the first fetch is in flight
 *   error           — non-null when the API returned a 5xx (callers should hide the panel)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../api/client';
import type { Recommendation, RecommendationsResponseData } from '../types';

const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: RecommendationsResponseData;
  fetchedAt: number;
}

let _cache: CacheEntry | null = null;

export interface UseRecommendationsResult {
  recommendations: Recommendation[];
  isLoading: boolean;
  error: boolean;
}

export function useRecommendations(): UseRecommendationsResult {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    // Serve from cache if fresh
    if (_cache && Date.now() - _cache.fetchedAt < STALE_TIME_MS) {
      setRecommendations(_cache.data.courses);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(false);
    try {
      const res = await apiClient.get<{ data: RecommendationsResponseData }>(
        '/agents/me/recommendations',
      );
      const data = res.data.data;
      _cache = { data, fetchedAt: Date.now() };

      if (isMounted.current) {
        setRecommendations(data.courses);
      }
    } catch {
      if (isMounted.current) {
        console.error('[useRecommendations] Failed to fetch recommendations');
        setError(true);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    void load();
    return () => {
      isMounted.current = false;
    };
  }, [load]);

  return { recommendations, isLoading, error };
}
